import { CardContent, CardFooter } from '@/shared/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type {
  LoginInitialRequestDto,
  LoginMfaRequiredResponseDto,
} from '@/features/auth-login';
import { useMFA } from '@/features/auth-login';
import { AuthCard } from '../../../shared/components/AuthCard';
import { ErrorText } from '@/shared/components';
import {
  MFA_IDENTITY_TYPES,
  MfaIdentitiesList,
  type MFAIdentityType,
  type MfaStateData,
} from '@/entities/mfa-identity';

type Props = {
  className?: string;
};

type MfaMethodsListLocationState = {
  loginArgs: LoginInitialRequestDto;
  identityTypes: LoginMfaRequiredResponseDto['data']['identityTypes'];
  mfaState: string;
  mfaData?: MfaStateData;
};

const LoginMfaMethodsList = ({ className }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { isMfaLoading, mfaError } = useMFA();

  if (
    !location.state ||
    !location.state.loginArgs ||
    !location.state.identityTypes ||
    !location.state.mfaState
  ) {
    return <Navigate to="/login" />;
  }

  const { loginArgs, mfaState, identityTypes, mfaData } =
    location.state as MfaMethodsListLocationState;

  // If no methods are available, redirect to login
  if (identityTypes.length === 0) {
    return <Navigate to="/login" />;
  }

  const selectMfaMethod = async (method: MFAIdentityType) => {
    if (method === MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR) {
      navigate('/login/mfa-code', {
        state: {
          loginArgs,
          mfaType: method,
          mfaState,
          identityTypes,
        },
      });
    } else {
      navigate('/login/mfa-code', {
        state: {
          loginArgs,
          mfaType: method,
          mfaState,
          identityTypes,
          mfaData,
        },
      });
    }
  };

  return (
    <AuthCard
      className={className}
      title={t('authPages.mfaMethodsList.title')}
      description={t('authPages.mfaMethodsList.description')}
      backButton={{
        link: '/login',
        state: {
          mfaData,
        },
      }}>
      <CardContent className="p-0">
        <MfaIdentitiesList
          availableIdentities={identityTypes}
          onSelectIdentity={selectMfaMethod}
          isLoading={isMfaLoading}
        />
      </CardContent>
      {mfaError && (
        <CardFooter className="mt-[1.6rem] px-0">
          <ErrorText size="small">{mfaError}</ErrorText>
        </CardFooter>
      )}
    </AuthCard>
  );
};

export { LoginMfaMethodsList };
